import type { FunctionComponent } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { EmptyStateMessage } from '~/ui/core/empty-state-message/EmptyStateMessage';

import { useUrlTileEscapeClose } from '../../../url-tiles';
import { useAutomationTileTask } from '../api/use-automation-tile-task';
import { profileTaskRichTextToPlainText } from '../profile-task-rich-text';
import { ProfileTaskBreadcrumbs } from '../tile/ProfileTaskBreadcrumbs';
import { TaskHeading } from '../tile/profile-task-tile/TaskHeading';
import { TaskTileError, TaskTileLoading } from '../tile/profile-task-tile/TaskTileFallbacks';
import { TaskTileFrame } from '../tile/profile-task-tile/TaskTileFrame';
import { TASK_AUDIT_BOX_ROWS_CLASS, TaskAuditBox, TaskMetaTooltip } from '../tile/TaskFieldPrimitives';
import { AutomationAiNotice } from './AutomationAssessmentSection';
import { AutomationRationale } from './AutomationRationale';
import { CAPABILITY_LEVELS, type CapabilityLevel, type StepAssessment } from './automation-assessment-model';
import { COORDINATION_HUMAN_SHARE, toPercent, verificationRetention } from './automation-assessment-scoring';
import { automationAssessmentTranslationKeys as keys } from './automation-assessment-translations';
import type { AutomationStepTilePayload } from './automation-step-tile-url';
import { toProfileContext } from './automation-tile-address';
import { FactorRow } from './FactorRow';

const CAPABILITY_TICKS = {
    FULLY_SPECIFIED: keys.capabilityFullySpecified,
    NEEDS_CONTEXT: keys.capabilityNeedsJudgment,
    NOT_WRITABLE: keys.capabilityNotWritable,
    ROUTINE: keys.capabilityRoutine,
} as const satisfies Record<CapabilityLevel, string>;

/** The four rated levels side by side, the step's level marked. */
const CapabilityScale: FunctionComponent<{ readonly level: CapabilityLevel }> = ({ level }) => {
    const translate = useTranslate();

    return (
        <div className="my-xs flex gap-xxs">
            {CAPABILITY_LEVELS.map((tick) => (
                <span
                    key={tick}
                    className={`flex-1 border-0 border-t-[3px] border-solid pt-xs text-center text-body-xxs ${
                        tick === level
                            ? 'border-text-secondary text-text-primary'
                            : 'border-border-soft text-text-content-faint'
                    }`}
                >
                    {translate(CAPABILITY_TICKS[tick])}
                </span>
            ))}
        </div>
    );
};

const StepFactors: FunctionComponent<{ readonly step: StepAssessment }> = ({ step }) => {
    const translate = useTranslate();
    const percent = (value: number): string => translate(keys.percent, { value });
    const peopleShare = step.coreInteractionShare + step.coordinationShare;
    const peopleKeep = step.coreInteractionShare + COORDINATION_HUMAN_SHARE * step.coordinationShare;
    const reviewPercent = toPercent(verificationRetention(step.errorCost, step.mandatedHumanInvolvement));
    const passesPercent = 100 - reviewPercent;

    return (
        <TaskAuditBox testId="automation-step-factors" title={translate(keys.factors)}>
            <ol className={`${TASK_AUDIT_BOX_ROWS_CLASS} list-none`}>
                <FactorRow
                    description={translate(keys.digitalShareDescription, { value: step.digitalShare })}
                    label={translate(keys.digitalShare)}
                    value={percent(step.digitalShare)}
                />
                <FactorRow
                    description={translate(keys.peopleDescription, {
                        coordination: step.coordinationShare,
                        core: step.coreInteractionShare,
                        keep: Math.round(peopleKeep),
                    })}
                    label={translate(keys.people)}
                    value={percent(peopleShare)}
                />
                <FactorRow
                    description={translate(keys.capabilityDescription)}
                    label={translate(keys.capability)}
                    value={translate(CAPABILITY_TICKS[step.capabilityLevel])}
                >
                    <CapabilityScale level={step.capabilityLevel} />
                </FactorRow>
                <FactorRow
                    description={translate(keys.passesWithoutReviewDescription, { review: reviewPercent })}
                    label={translate(keys.passesWithoutReview)}
                    value={percent(passesPercent)}
                />
                <FactorRow
                    description={translate(
                        step.mandatedHumanInvolvement ? keys.mandatedYesDescription : keys.mandatedNoDescription,
                    )}
                    label={translate(keys.mandated)}
                    value={translate(step.mandatedHumanInvolvement ? keys.yes : keys.no)}
                />
            </ol>
        </TaskAuditBox>
    );
};

/** The step's score as a number over a thin bar, with its weight in the task beside it. */
const StepScore: FunctionComponent<{ readonly step: StepAssessment }> = ({ step }) => {
    const translate = useTranslate();
    const score = toPercent(step.score);
    const effort = toPercent(step.normalizedEffortShare);

    return (
        <div className="flex flex-col gap-s" data-testid="automation-step-score">
            <div className="flex items-baseline gap-s">
                <span className="text-h3 font-medium tabular-nums text-text-primary">
                    {translate(keys.percent, { value: score })}
                </span>
                <span className="text-body-xs text-text-muted">{translate(keys.title)}</span>
                <TaskMetaTooltip
                    className="ml-auto text-body-xs"
                    explanation={<span>{translate(keys.effortShareDescription, { value: effort })}</span>}
                    heading={translate(keys.effortShare)}
                >
                    {translate(keys.effortShare)}{' '}
                    <span className="tabular-nums text-text-primary">{translate(keys.percent, { value: effort })}</span>
                </TaskMetaTooltip>
            </div>
            <div aria-hidden={true} className="relative h-1.5 overflow-hidden rounded-full bg-border-soft">
                <span
                    className="absolute inset-y-0 left-0 rounded-full bg-text-secondary"
                    style={{ width: `${score}%` }}
                />
            </div>
        </div>
    );
};

export const AutomationStepTile: FunctionComponent<{
    readonly onClose: () => void;
    readonly payload: AutomationStepTilePayload;
}> = ({ onClose, payload }) => {
    const translate = useTranslate();
    useUrlTileEscapeClose(onClose);
    const context = toProfileContext(payload);
    const query = useAutomationTileTask({
        context,
        language: payload.language,
        taskId: payload.taskId,
        taskVersionId: payload.taskVersionId,
    });

    if (query.isLoading) {
        return (
            <TaskTileFrame onClose={onClose}>
                <TaskTileLoading />
            </TaskTileFrame>
        );
    }

    if (query.isError || query.data === undefined) {
        return (
            <TaskTileFrame onClose={onClose}>
                <TaskTileError />
            </TaskTileFrame>
        );
    }

    const task = query.data;
    const assessment = task.assessment;
    const workflowStep = task.workflowSteps.find(({ workflowStepId }) => workflowStepId === payload.workflowStepId);
    const step = assessment?.steps.find(({ workflowStepId }) => workflowStepId === payload.workflowStepId);

    if (workflowStep === undefined) {
        return (
            <TaskTileFrame onClose={onClose}>
                <TaskTileError />
            </TaskTileFrame>
        );
    }

    return (
        <TaskTileFrame testId="automation-step-tile" onClose={onClose}>
            <div className="flex flex-col gap-m">
                {context === undefined ? null : (
                    <ProfileTaskBreadcrumbs context={context} currentItemTitle={translate(keys.stepBreadcrumb)} />
                )}
                <TaskHeading title={workflowStep.title} />
                <p className="m-0 text-body-xs text-text-secondary">
                    {profileTaskRichTextToPlainText(workflowStep.description)}
                </p>
            </div>
            {step === undefined ? (
                <EmptyStateMessage>{translate(keys.notAssessed)}</EmptyStateMessage>
            ) : (
                <div className="flex flex-col gap-l">
                    <StepScore step={step} />
                    <AutomationRationale>{step.rationale}</AutomationRationale>
                    <StepFactors step={step} />
                    <AutomationAiNotice />
                </div>
            )}
        </TaskTileFrame>
    );
};
